import axios from "../../../../api/axios";

/**
 * API endpoints for inventory and MRO supplier operations
 * @constant {Object} ENDPOINTS
 */
export const ENDPOINTS = {
  GET_PROPERTIES: "v2/inventory/property-list",
  GET_INVENTORY_ITEMS: "v2/inventory",
  GET_INVENTORY_ORDERS: "v2/inventory/order",
  GET_INVENTORY_ORDERS_FOR_RM_VENDOR: "v2/inventory/order/vendor-orders",
  CREATE_INVENTORY_ORDER: "v2/inventory/order",
  UPDATE_INVENTORY_ORDER: "v2/inventory/order/:id",
  DELETE_INVENTORY_ORDER: "v2/inventory/order/:id",
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {void} Throws an error with a formatted message
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  throw new Error(message);
};

/**
 * Fetches property list based on user role and ID
 * @param {string} role - User role
 * @param {string} id - User ID
 * @returns {Promise<Object>} Filtered property data with propertyList and building_list
 */
export const getPropertyList = async (role, id) => {
  try {
    const { data } = await axios.get(
      `${ENDPOINTS.GET_PROPERTIES}?role=${role || ""}&id=${id || ""}`
    );
    const properties = data.data || [];
    return {
      propertyList: properties,
      building_list: properties.length ? properties[0].building || [] : [],
    };
  } catch (error) {
    handleApiError(error, "Failed to fetch property list");
  }
};

/**
 * Fetches all inventory items
 * @param {string} property_id - The property ID to filter inventory
 * @param {string} building_name - The building name to filter inventory
 * @returns {Promise<Object>} Inventory items data
 */
export const getInventoryItems = async (property_id, building_name) => {
  try {
    const { data } = await axios.get(ENDPOINTS.GET_INVENTORY_ITEMS, {
      params: { property_id, building_name },
    });
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inventory items");
  }
};

/**
 * Fetches all inventory orders
 * @param {string} propertyId - The property ID to filter orders
 * @param {string} buildingName - The building name to filter orders
 * @returns {Promise<Object>} Inventory orders data
 */
export const getInventoryOrders = async (propertyId, buildingName) => {
  try {
    const { data: { data } } = await axios.get(ENDPOINTS.GET_INVENTORY_ORDERS, {
      params: { propertyId, buildingName }
    });
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inventory orders");
  }
};

/**
 * Fetches inventory orders for a specific RM vendor
 * @param {string} vendorId - The vendor ID to filter orders
 * @param {Object} [filters] - Optional filters for orders
 * @param {string} [filters.propertyId] - Property ID to filter orders
 * @param {string} [filters.buildingName] - Building name to filter orders
 * @param {string} [filters.deliveryStatus] - Delivery status to filter orders
 * @param {string} [filters.orderDateFrom] - Start date for order date range
 * @param {string} [filters.orderDateTo] - End date for order date range
 * @param {number} [filters.page=1] - Page number for pagination
 * @param {number} [filters.limit=10] - Number of orders per page
 * @param {string} [filters.sortBy='orderDetails.ordered.date'] - Field to sort by
 * @param {string} [filters.sortOrder='desc'] - Sort order (asc or desc)
 * @returns {Promise<Object>} Inventory orders data for the specified vendor
 */
export const getInventoryOrdersForRMVendor = async (vendorId, filters = {}) => {
  try {
    if (!vendorId) {
      throw new Error("Vendor ID is required");
    }

    const {
      propertyId,
      buildingName,
      deliveryStatus,
      orderDateFrom,
      orderDateTo,
      page = 1,
      limit = 10,
      sortBy = 'orderDetails.ordered.date',
      sortOrder = 'desc'
    } = filters;

    const { data: { data } } = await axios.get(ENDPOINTS.GET_INVENTORY_ORDERS_FOR_RM_VENDOR, {
      params: {
        vendorId,
        propertyId,
        buildingName,
        deliveryStatus,
        orderDateFrom,
        orderDateTo,
        page,
        limit,
        sortBy,
        sortOrder
      }
    });
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inventory orders for RM vendor");
  }
};

/**
 * Creates a new inventory order
 * @param {Object} payload - Data for the new inventory order
 * @returns {Promise<Object>} Created inventory order data
 */
export const createInventoryOrder = async (payload) => {
  try {
    const { data } = await axios.post(ENDPOINTS.CREATE_INVENTORY_ORDER, payload);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to create inventory order");
  }
};

/**
 * Updates an existing inventory order
 * @param {string} id - Inventory order ID
 * @param {Object} payload - Updated data for the inventory order
 * @returns {Promise<Object>} Updated inventory order data
 */
export const updateInventoryOrder = async (id, payload) => {
  try {
    const url = ENDPOINTS.UPDATE_INVENTORY_ORDER.replace(":id", id);
    const { data } = await axios.put(url, payload);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to update inventory order");
  }
};

/**
 * Deletes an inventory order by ID
 * @param {string} id - Inventory order ID
 * @returns {Promise<Object>} Response after deletion
 */
export const deleteInventoryOrder = async (id) => {
  try {
    if (!id) {
      throw new Error("Order ID is required");
    }

    const url = ENDPOINTS.DELETE_INVENTORY_ORDER.replace(":id", id);
    const { data } = await axios.delete(url);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to delete inventory order");
  }
};

/**
 * Toggles the delivery status of an inventory order
 * @param {string} id - Inventory order ID
 * @param {string} deliveryStatus - New delivery status to set
 * @returns {Promise<Object>} Updated inventory order data
 */
export const toggleInventoryOrderDeliveryStatus = async (id, deliveryStatus) => {
  try {
    if (!id || !deliveryStatus) {
      throw new Error("Order ID and delivery status are required");
    }

    const url = `${ENDPOINTS.UPDATE_INVENTORY_ORDER.replace(":id", id)}/delivery-status`;
    const { data } = await axios.patch(url, { deliveryStatus });

    return data;
  } catch (error) {
    handleApiError(error, "Failed to toggle inventory order delivery status");
  }
};